import { Component, AfterViewInit, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';
import { DOCUMENT, CommonModule } from '@angular/common';

@Component({
  selector: 'app-ai-agents-in-enterprises',
  imports: [],
  standalone: true,
  templateUrl: './ai-agents-in-enterprises.component.html',
  styleUrl: './ai-agents-in-enterprises.component.scss'
})
export class AiAgentsInEnterprisesComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  nav(url: any) {
    this.router.navigateByUrl(url)
  }
  injectSchemaMarkup(): void {
    const aiAgentsSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises",
          "name": "Rise of AI Agents in Enterprises: How Human-Centric Approach Drives Success | Kalpita Technologies",
          "description": "Comprehensive guide on AI agents in enterprises covering human-centric transformation, governance, explainable AI, and multi-agent ecosystems.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog Index" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises", "name": "AI Agents in Enterprises" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises#blogposting",
          "headline": "Rise of AI Agents in Enterprises: How Human-Centric Approach Drives Success",
          "articleBody": "The artificial intelligence revolution has reached a critical inflection point. We've evolved from simple chatbots and predictive models to autonomous AI agents...",
          "author": [
            { "@type": "Person", "name": "Bhavani" },
            { "@type": "Person", "name": "Roshan" },
            { "@type": "Person", "name": "Grace" }
          ],
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "@id": "https://www.kalpitatechnologies.com/#organization"
          },
          "datePublished": "2025-11-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/ai-agents-in-enterprises/rise-of-ai-agent-banner.webp"
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises#howto",
          "name": "How to Implement Human-Centric AI Agents in Enterprise Workflows",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Process Standardization", "text": "Map, clean, and re-engineer core workflows before automation." },
            { "@type": "HowToStep", "position": 2, "name": "Task Augmentation", "text": "Introduce AI agents in supportive roles handling discrete, high-volume tasks." },
            { "@type": "HowToStep", "position": 3, "name": "Workflow Orchestration", "text": "Empower central AI agents to manage complete, multi-step processes." },
            { "@type": "HowToStep", "position": 4, "name": "Strategic Autonomy", "text": "Enable agents to proactively identify opportunities and risks." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/ai-agents-in-enterprises#faqpage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the difference between AI agents and traditional automation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Traditional automation follows fixed if-then rules, while AI agents use machine learning and LLMs to understand context and proactive goals."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/resources/kalpita-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(aiAgentsSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  @ViewChild('tableresponsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;

  originalWidth = 3840;
  originalHeight = 1720;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap(): void {
    const img = this.image.nativeElement;
    if (!img) return;

    const wRatio = img.clientWidth / this.originalWidth;
    const hRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll<HTMLAreaElement>('map[name="table-image-map"] area')
      .forEach(area => {
        const rawCoords =
          area.getAttribute('data-original-coords')
          || area.getAttribute('coords')
          || '';
        if (!rawCoords) return;

        area.setAttribute('data-original-coords', rawCoords);
        const scaled = rawCoords
          .split(',')
          .map((c, i) =>
            Math.round(
              Number(c) * (i % 2 === 0 ? wRatio : hRatio)
            )
          )
          .join(',');

        area.setAttribute('coords', scaled);
      });
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top =
      el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
